/** TanStack Query hooks for personal lesson folders: CRUD and move-to-folder. */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { PersonalLessonFolder } from '@formency/shared';
import { personalLessonKeys } from './usePersonalLessons';

// ─── Query key factory ─────────────────────────────────────────────────────

export const folderKeys = {
  all: ['f', 'personal-lesson-folders'] as const,
  list: () => [...folderKeys.all, 'list'] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch all personal lesson folders for the current user. */
export function useFolders() {
  return useQuery<PersonalLessonFolder[]>({
    queryKey: folderKeys.list(),
    queryFn: () => api.get<PersonalLessonFolder[]>('/me/personal-lessons/folders'),
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Create a new personal lesson folder. */
export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation<PersonalLessonFolder, Error, { name: string; color?: string | null }>({
    mutationFn: ({ name, color }) =>
      api.post<PersonalLessonFolder>('/me/personal-lessons/folders', { name, color }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

/** Rename an existing personal lesson folder. */
export function useRenameFolder() {
  const queryClient = useQueryClient();
  return useMutation<
    PersonalLessonFolder,
    Error,
    { folderId: string; name: string; color?: string | null }
  >({
    mutationFn: ({ folderId, name, color }) =>
      api.patch<PersonalLessonFolder>(`/me/personal-lessons/folders/${folderId}`, { name, color }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

/** Delete a personal lesson folder. Lessons inside are moved to unfiled. */
export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (folderId) => api.del(`/me/personal-lessons/folders/${folderId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
      void queryClient.invalidateQueries({ queryKey: personalLessonKeys.all });
    },
  });
}

/** Move a personal lesson into a folder (or unfiled when folderId is null). */
export function useMoveToFolder() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { lessonId: string; folderId: string | null }>({
    mutationFn: ({ lessonId, folderId }) =>
      api.patch(`/me/personal-lessons/${lessonId}/folder`, { folderId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: personalLessonKeys.all });
      void queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}
